import React, {useCallback, useContext} from 'react';
import {useSelector} from 'react-redux';

import {MessageContext} from 'src/contexts/message-context';
import {selectAccountLicense} from 'src/modules/profile/account/tabs/license-and-features/services/account-license-features-slice';
import {getError} from 'src/utils/get-error';

import Button from '@pb/kit-component-button';
import {CheckBox} from '@pb/kit-component-checkbox';
import FormSection from '@pb/kit-component-form-section';
import {Group} from '@pb/kit-component-group';
import {Icons} from '@pb/kit-component-icon';
import {BasicIconButton} from '@pb/kit-component-icon-button';
import Loading, {Types as LoadingTypes} from '@pb/kit-component-loading';
import {Tooltip} from '@pb/kit-component-popups';
import {Body, Cell, Head, Row, Table} from '@pb/kit-component-table';


import {ImportSyncedEntities} from './import-synced-entities';

import type {Dispatch, SetStateAction} from 'react';
import type {MessageContextType} from 'src/contexts/message-context';
import type {CrmType} from 'src/openapi/admin/models/CrmType';
import type {Error} from 'src/openapi/admin/models/Error';
import type {HubspotAccountSettings} from 'src/openapi/admin/models/HubspotAccountSettings';
import type {ImportEntitySettings} from 'src/openapi/admin/models/ImportEntitySettings';
import type {LinkageStatistics} from 'src/openapi/admin/models/LinkageStatistics';
import type {CheckBoxChangeData} from 'src/typedef';

type HubspotEntityKey = Exclude<keyof HubspotAccountSettings, 'alertsEnabled'>;

type Props = {
    crmType: CrmType,
    disabled?: boolean,
    isSyncLinkageLoading: boolean,
    isLinkageStatisticLoading: boolean,
    linkageStatisticLoadingError: Error | null,

    settings: HubspotAccountSettings,
    linkageStatistic: LinkageStatistics | null,
    orgId?: string,

    onSyncLinkageStatistic: () => void,
    onChangeSettings: Dispatch<SetStateAction<HubspotAccountSettings>>,
};

export const CommonEntitiesSection = (props: Props) => {
    const license = useSelector(selectAccountLicense);
    const {setErrorMessage} = useContext<MessageContextType>(MessageContext);

    const {
        crmType,
        disabled,
        isSyncLinkageLoading,
        isLinkageStatisticLoading,
        linkageStatisticLoadingError,

        linkageStatistic,
        settings,
        orgId,

        onSyncLinkageStatistic,
        onChangeSettings,
    } = props;

    const changeSettings = useCallback((changeData: CheckBoxChangeData) => {
        const [entity, property] = changeData.name.split('-') as [HubspotEntityKey, keyof ImportEntitySettings];

        onChangeSettings(prevState => ({
            ...prevState,
            [entity]: {
                ...prevState[entity],
                [property]: changeData.event.currentTarget.checked,
            },
        }));
    }, [onChangeSettings]);

    const changeAlerts = useCallback((changeData: CheckBoxChangeData) => {
        onChangeSettings(prevState => ({
            ...prevState,
            alertsEnabled: changeData.event.currentTarget.checked,
        }));
    }, [onChangeSettings]);

    return (
        <FormSection headline="Entities">
            <Group direction="v">
                <CheckBox
                    disabled={disabled}
                    label="Enable alerts"
                    checked={settings.alertsEnabled}
                    onCheckBoxChange={changeAlerts}
                />
                <Table borderedGrid={true}>
                    <Head>
                        <Cell>Entity type</Cell>
                        <Cell className="text-align_center">
                            <Group justify="center">
                                {isLinkageStatisticLoading ? <Loading type={LoadingTypes.ICON}/> : null}
                                {linkageStatisticLoadingError ? (
                                    <BasicIconButton
                                        iconProps={{type: Icons.ERROR}}
                                        onClick={() => {
                                            setErrorMessage(getError({
                                                error: linkageStatisticLoadingError,
                                            }));
                                        }}
                                    />
                                ) : null}
                                Linked PB IDs
                            </Group>
                        </Cell>
                        <Cell className="text-align_center">
                            <Group justify="center">
                                {isLinkageStatisticLoading ? <Loading type={LoadingTypes.ICON}/> : null}
                                {linkageStatisticLoadingError ? (
                                    <BasicIconButton
                                        iconProps={{type: Icons.ERROR}}
                                        onClick={() => {
                                            setErrorMessage(getError({
                                                error: linkageStatisticLoadingError,
                                            }));
                                        }}
                                    />
                                ) : null}
                                Synced PB IDs
                            </Group>
                        </Cell>
                        <Cell className="text-align_center">Autolink</Cell>
                        <Cell className="text-align_center">Autosync</Cell>
                        <Cell className="text-align_center">Import enabled</Cell>
                    </Head>
                    <Body>
                        <Row>
                            <Cell>Contacts</Cell>
                            <Cell className="text-align_center">{linkageStatistic?.contactsPbIdsLinked.toString()}</Cell>
                            <Cell className="text-align_center">{linkageStatistic?.contactsPbIdsSynced.toString()}</Cell>
                            <Cell>
                                <Group justify="center">
                                    <CheckBox
                                        disabled={disabled}
                                        checked={settings.contactSettings.autoLinkEnabled}
                                        onCheckBoxChange={changeSettings}
                                        name="contactSettings-autoLinkEnabled"
                                    />
                                </Group>
                            </Cell>
                            <Cell>
                                <Group justify="center">
                                    <CheckBox
                                        disabled={disabled}
                                        checked={settings.contactSettings.autoSyncEnabled}
                                        onCheckBoxChange={changeSettings}
                                        name="contactSettings-autoSyncEnabled"
                                    />
                                </Group>
                            </Cell>
                            <Cell>
                                <Group justify="center">
                                    <CheckBox
                                        disabled={disabled}
                                        checked={settings.contactSettings.importEnabled}
                                        onCheckBoxChange={changeSettings}
                                        name="contactSettings-importEnabled"
                                    />
                                </Group>
                            </Cell>
                        </Row>
                        <Row>
                            <Cell>Accounts</Cell>
                            <Cell className="text-align_center">{linkageStatistic?.accountsPbIdsLinked.toString()}</Cell>
                            <Cell className="text-align_center">{linkageStatistic?.accountsPbIdsSynced.toString()}</Cell>
                            <Cell>
                                <Group justify="center">
                                    <CheckBox
                                        disabled={disabled}
                                        checked={settings.accountSettings.autoLinkEnabled}
                                        onCheckBoxChange={changeSettings}
                                        name="accountSettings-autoLinkEnabled"
                                    />
                                </Group>
                            </Cell>
                            <Cell>
                                <Group justify="center">
                                    <CheckBox
                                        disabled={disabled}
                                        checked={settings.accountSettings.autoSyncEnabled}
                                        onCheckBoxChange={changeSettings}
                                        name="accountSettings-autoSyncEnabled"
                                    />
                                </Group>
                            </Cell>
                            <Cell>
                                <Group justify="center">
                                    <CheckBox
                                        disabled={disabled}
                                        checked={settings.accountSettings.importEnabled}
                                        onCheckBoxChange={changeSettings}
                                        name="accountSettings-importEnabled"
                                    />
                                </Group>
                            </Cell>
                        </Row>
                    </Body>
                </Table>
                <Group>
                    <Tooltip
                        color="light"
                        anchor={({anchorRef, getAnchorProps}) => (
                            <Button
                                ref={anchorRef}
                                {...getAnchorProps()}
                                busy={license?.isActive ? isSyncLinkageLoading : undefined}
                                disabled={disabled || !license?.isActive || !orgId}
                                variant="Type.SECONDARY"
                                onClick={onSyncLinkageStatistic}>
                                Force Reconciliation
                            </Button>
                        )}>
                        Force Entity Linkage Reconciliation
                    </Tooltip>
                    <ImportSyncedEntities
                        crmType={crmType}
                        disabled={disabled || !orgId}
                        targetLicenseId={license?.licenseId || 0}
                    />
                </Group>
            </Group>
        </FormSection>
    );
};
